import * as React from "react";
import { useEffect } from "react";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { Button } from "@/lib/shadcn/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/lib/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/lib/shadcn/ui/popover";
import { cn } from "@/lib/shadcn/utils";

export interface SelectOption {
    value: string;
    label: string;
}

interface SelectProps {
    options: SelectOption[];
    onChange: (value: string) => void;
    placeholder: string;
    selectedValue?: string;
    selectedValuePrefix?: string;
    variant?: "default" | "destructive" | "outline" | "secondary" | "volt" | "ghost" | "link";
    className?: string;
    width?: string;
    disabled?: boolean;
    /** Optional test id placed on the trigger button (for component tests). */
    "data-testid"?: string;
    /**
     * Optional id placed on the trigger button so an external `<label htmlFor>`
     * can associate with the control (WCAG 1.3.1).
     */
    id?: string;
    /** Optional accessible name for the trigger button (WCAG 4.1.2). */
    "aria-label"?: string;
    /** Marks the control as required for assistive tech (WCAG 1.3.1/4.1.2). */
    "aria-required"?: boolean;
    /** Ids of elements describing the control, e.g. a format hint (WCAG 1.3.1). */
    "aria-describedby"?: string;
    /**
     * Optional handle on the trigger button, so a caller that opens a panel from
     * this control's `footer` can hand the keyboard BACK to it when that panel
     * closes (WCAG 2.4.3) — the footer button itself is gone by then, along with
     * the popover it lived in.
     */
    triggerRef?: React.Ref<HTMLButtonElement>;
    /**
     * Optional cmdk scoring function `(optionLabel, search, keywords) => score`;
     * a score of 0 hides the option. Defaults to cmdk's own fuzzy match on the
     * raw label. Supplied by callers whose options need a domain-specific match
     * — e.g. the address control, which folds Greek/Latin look-alikes and
     * accents so a query can find a building it does not spell identically.
     */
    filter?: (value: string, search: string, keywords?: string[]) => number;
    /**
     * Optional content pinned BELOW the option list, inside the popover and
     * outside cmdk's filtering — so it survives a query that matches nothing.
     * Receives a `close` callback, since the popover owns its own open state.
     *
     * Added for the address control's "＋ Add a new address" affordance
     * (address-normalization/FR-011): the operator discovers a building is
     * missing precisely when the list is empty, which is exactly when a
     * `CommandItem` would have been filtered away.
     */
    footer?: (close: () => void) => React.ReactNode;
}

export const SearchableSelect: React.FC<SelectProps> = ({
    options,
    onChange,
    placeholder,
    variant = "default",
    className,
    width = "w-[200px]",
    selectedValue,
    selectedValuePrefix,
    disabled = false,
    "data-testid": dataTestId,
    id,
    "aria-label": ariaLabel,
    "aria-required": ariaRequired,
    "aria-describedby": ariaDescribedBy,
    triggerRef,
    filter,
    footer,
}) => {
    const [open, setOpen] = React.useState(false);
    const [value, setValue] = React.useState(selectedValue || "");
    // Stable id so the trigger can advertise the popover it controls
    // (`aria-controls`) — required for the ARIA 1.2 combobox pattern.
    const popoverId = React.useId();

    useEffect(() => {
        if (selectedValue) {
            setValue(selectedValue);
        }
    }, [selectedValue]);

    const getCaretIconClasses = (variant: SelectProps["variant"]) => {
        const baseClasses = "ml-2 h-4 w-4 shrink-0";

        const variantClasses = {
            default: "text-primary-foreground",
            destructive: "text-destructive-foreground",
            outline: "text-foreground",
            secondary: "text-secondary-foreground",
            volt: "text-volt-foreground",
            ghost: "text-foreground",
            link: "text-primary",
        };

        return cn(baseClasses, variantClasses[variant || "default"], "transition-transform duration-200", open && "transform rotate-180");
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    ref={triggerRef}
                    id={id}
                    variant={variant}
                    role="combobox"
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    aria-controls={popoverId}
                    aria-label={ariaLabel}
                    aria-required={ariaRequired}
                    aria-describedby={ariaDescribedBy}
                    className={cn(width, "justify-between text-left [&>div]:truncate", className)}
                    disabled={disabled}
                    data-testid={dataTestId}
                >
                    <div className="flex gap-1">
                        {selectedValuePrefix && <span className="truncate">{selectedValuePrefix}: </span>}
                        <span className="truncate">{value ? options.find((option) => option.value === value)?.label : placeholder}</span>
                    </div>
                    <CaretSortIcon className={getCaretIconClasses(variant)} />
                </Button>
            </PopoverTrigger>
            <PopoverContent id={popoverId} className={cn(width, "p-0")}>
                <Command filter={filter}>
                    <CommandInput placeholder={placeholder} className="h-9" />
                    <CommandList>
                        <CommandEmpty>No option found.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.label}
                                    onSelect={() => {
                                        if (option.value !== value) {
                                            setValue(option.value);
                                            onChange(option.value);
                                        }
                                        setOpen(false);
                                    }}
                                    data-value={option.value}
                                    data-cmdk-item={option.label.toLowerCase()}
                                >
                                    {option.label}
                                    <CheckIcon
                                        aria-hidden="true"
                                        className={cn("ml-auto h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    {footer?.(() => setOpen(false))}
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default SearchableSelect;
